// src/components/CreateMemorandumModal.tsx - UPDATED WITH CLIENT-SIDE PDF DOWNLOAD
import React, { useState, useEffect } from 'react';
import { X, FileText, Send, AlertCircle, Info, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { getAspirantId, API_BASE_URL } from '../services/api';

interface CreateMemorandumModalProps {
  onClose: () => void;
  onSuccess?: (memorandumData?: any) => void;
}

export interface MemorandumRequest {
  aspirantId: number;
  type: string;
  recipientDepartment: string;
  subject: string;
  content: string;
  evidenceSummary: string;
  affectedWards: string[];
  estimatedCost: number;
  costBreakdown?: string;
  useTemplate?: boolean;
  templateName?: string;
}

export interface MemorandumFormData {
  subject: string;
  recipientDepartment: string;
  evidenceSummary: string;
  affectedWards: string[];
  estimatedCost: number;
  actions: string;
  type: string;
}

// Wards list
const WARDS = [
  'London Ward', 'Kapkures Ward', 'Biashara Ward', 'Kaptembwo', 
  'Naivasha East', 'Gilgil', 'Njoro', 'Molo', 'Kuresoi South',
  'Subukia', 'Rongai', 'Bahati', 'Elementaita Ward', 'Naivasha West'
];

// Recipient Departments
const RECIPIENT_DEPARTMENTS = [
  'The CECM, Water, Environment & Natural Resources',
  'The CECM, Transport & Infrastructure',
  'The CECM, Health Services',
  'The CECM, Education, ICT & Youth Affairs',
  'The CECM, Lands, Housing & Physical Planning',
  'The CECM, Agriculture, Livestock & Fisheries',
  'The CECM, Trade, Tourism & Cooperatives'
];

// ============ HELPER FUNCTIONS ============

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

// ============ PDF DOWNLOAD HELPERS ============

/**
 * Download memorandum as PDF (client-side generation - no backend needed)
 * Uses jsPDF like manifesto download
 */
const downloadMemorandumAsPDF = async (memorandum: any) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(memorandum.subject, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Memo number and date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Memorandum Number: ${memorandum.number}`, margin, yPos);
    yPos += 5;
    doc.text(`Generated Date: ${new Date(memorandum.generatedAt).toLocaleDateString()}`, margin, yPos);
    yPos += 5;
    doc.text(`Status: ${memorandum.status.charAt(0).toUpperCase() + memorandum.status.slice(1)}`, margin, yPos);
    yPos += 10;

    // Recipient
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Recipient:', margin, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const recipientLines = doc.splitTextToSize(memorandum.recipient, pageWidth - margin * 2);
    doc.text(recipientLines, margin, yPos);
    yPos += recipientLines.length * 5 + 5;

    // Affected Wards
    if (memorandum.affectedWards && memorandum.affectedWards.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Affected Wards:', margin, yPos);
      yPos += 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const wardsText = memorandum.affectedWards.join(', ');
      const wardsLines = doc.splitTextToSize(wardsText, pageWidth - margin * 2);
      doc.text(wardsLines, margin, yPos);
      yPos += wardsLines.length * 5 + 5;
    }

    // Evidence Summary
    if (memorandum.evidence) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Evidence Summary:', margin, yPos);
      yPos += 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const evidenceLines = doc.splitTextToSize(memorandum.evidence, pageWidth - margin * 2);
      doc.text(evidenceLines, margin, yPos);
      yPos += evidenceLines.length * 5 + 5;
    }

    // Estimated Cost
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Estimated Cost:', margin, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(formatCurrency(memorandum.cost), margin, yPos);
    yPos += 10;

    // Content/Details
    if (memorandum.content) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Details:', margin, yPos);
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const contentLines = doc.splitTextToSize(memorandum.content, pageWidth - margin * 2);
      for (let i = 0; i < contentLines.length; i++) {
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(contentLines[i], margin, yPos);
        yPos += 4;
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.text(`Document ID: ${memorandum.id}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.save(`${memorandum.number.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
    toast.success('Memorandum downloaded as PDF!');
    return true;
  } catch (pdfError) {
    console.warn('PDF generation failed, falling back to TXT:', pdfError);
    return downloadMemorandumAsTXT(memorandum);
  }
};

/**
 * Download memorandum as TXT (fallback)
 */
const downloadMemorandumAsTXT = (memorandum: any) => {
  try {
    let content = `========================================\n`;
    content += `${memorandum.subject.toUpperCase()}\n`;
    content += `========================================\n\n`;
    content += `MEMORANDUM NUMBER: ${memorandum.number}\n`;
    content += `GENERATED DATE: ${new Date(memorandum.generatedAt).toLocaleDateString()}\n`;
    content += `STATUS: ${memorandum.status.toUpperCase()}\n`;
    content += `\n`;

    content += `RECIPIENT:\n${memorandum.recipient}\n\n`;

    if (memorandum.affectedWards && memorandum.affectedWards.length > 0) {
      content += `AFFECTED WARDS:\n${memorandum.affectedWards.join(', ')}\n\n`;
    }

    if (memorandum.evidence) {
      content += `EVIDENCE SUMMARY:\n${memorandum.evidence}\n\n`;
    }

    content += `ESTIMATED COST:\n${formatCurrency(memorandum.cost)}\n\n`;

    if (memorandum.content) {
      content += `DETAILS:\n${memorandum.content}\n\n`;
    }

    content += `========================================\n`;
    content += `END OF MEMORANDUM\n`;
    content += `Document ID: ${memorandum.id}\n`;
    content += `Generated by PamojaConnect Platform\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${memorandum.number.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Memorandum downloaded as TXT file!');
    return true;
  } catch (error) {
    console.error('TXT download failed:', error);
    toast.error('Failed to download memorandum');
    return false;
  }
};

/**
 * Handle memorandum download (tries PDF first, falls back to TXT)
 */
const handleDownloadMemorandum = async (memorandum: any) => {
  toast.loading('Preparing download...');
  try {
    const success = await downloadMemorandumAsPDF(memorandum);
    if (success) {
      toast.dismiss();
      return;
    }
  } catch (error) {
    toast.dismiss();
    toast.loading('Falling back to text format...');
    try {
      const txtSuccess = downloadMemorandumAsTXT(memorandum);
      if (txtSuccess) {
        toast.dismiss();
        toast.success('Downloaded as text file');
      }
    } catch (txtError) {
      toast.dismiss();
      toast.error('Download failed. Please try again.');
    }
  }
};

// ============ MAIN COMPONENT ============

const CreateMemorandumModal: React.FC<CreateMemorandumModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<MemorandumFormData>({
    subject: '',
    recipientDepartment: '',
    evidenceSummary: '',
    affectedWards: [],
    estimatedCost: 2500000,
    actions: 'Deploy emergency response teams to affected areas\nRepair identified infrastructure\nDevelop sustainable solutions',
    type: 'COUNTY_GOVERNMENT'
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [aspirantId, setAspirantId] = useState<number | null>(null);
  const [aspirantInfo, setAspirantInfo] = useState<any>({});

  useEffect(() => {
    // Get aspirant ID using the helper function
    const id = getAspirantId();
    if (id) {
      setAspirantId(id);
      console.log('✅ Using aspirant ID:', id);
      
      // Get aspirant info from localStorage
      const aspirantStr = localStorage.getItem('aspirant');
      const userStr = localStorage.getItem('user');
      
      if (aspirantStr) {
        try {
          const aspirantData = JSON.parse(aspirantStr);
          setAspirantInfo({
            name: aspirantData.name || aspirantData.fullName || 'Aspirant',
            position: aspirantData.position || 'Aspirant',
            phone: aspirantData.phoneNumber || aspirantData.phone || '[Your Number]',
            email: aspirantData.email || '[Your Email]',
            ward: aspirantData.ward || '[Your Ward]',
            constituency: aspirantData.constituency || '[Your Constituency]'
          });
        } catch (e) {
          console.log('Error parsing aspirant data:', e);
        }
      } else if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          setAspirantInfo({
            name: userData.fullName || userData.name || 'Aspirant',
            position: userData.position || 'Aspirant',
            phone: userData.phoneNumber || userData.phone || '[Your Number]',
            email: userData.email || '[Your Email]'
          });
        } catch (e) {
          console.log('Error parsing user data:', e);
        }
      }
    } else {
      toast.error('Please login as an aspirant first');
      setTimeout(() => onClose(), 2000);
    }
  }, [onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWardToggle = (ward: string) => {
    setFormData(prev => ({
      ...prev,
      affectedWards: prev.affectedWards.includes(ward)
        ? prev.affectedWards.filter(w => w !== ward)
        : [...prev.affectedWards, ward]
    }));
  };

  const handleActionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, actions: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    
    if (!formData.recipientDepartment.trim()) {
      toast.error('Recipient department is required');
      return;
    }
    
    if (!formData.evidenceSummary.trim()) {
      toast.error('Evidence summary is required');
      return;
    }
    
    if (formData.affectedWards.length === 0) {
      toast.error('Please select at least one affected ward');
      return;
    }
    
    if (!aspirantId) {
      toast.error('Aspirant ID not found. Please login again.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const memorandumData: MemorandumRequest = {
        aspirantId: aspirantId,
        type: 'COUNTY_GOVERNMENT',
        recipientDepartment: formData.recipientDepartment,
        subject: formData.subject,
        content: formData.actions,
        evidenceSummary: formData.evidenceSummary,
        affectedWards: formData.affectedWards,
        estimatedCost: formData.estimatedCost,
        costBreakdown: `Estimated breakdown: Emergency response (${Math.round(formData.estimatedCost * 0.3).toLocaleString()}), Infrastructure (${Math.round(formData.estimatedCost * 0.5).toLocaleString()}), Long-term solutions (${Math.round(formData.estimatedCost * 0.2).toLocaleString()})`,
        useTemplate: true,
        templateName: 'default'
      };

      console.log('📤 Submitting memorandum with aspirant ID:', aspirantId);
      console.log('📦 Request data:', memorandumData);

      // Use direct fetch instead of axios
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/memorandums`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(memorandumData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('aspirant');
          setTimeout(() => {
            window.location.href = '/aspirant/login';
          }, 1500);
          return;
        }
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const responseData = await response.json();
      console.log('✅ Memorandum creation response:', responseData);

      if (responseData.success) {
        toast.success('Memorandum created successfully!');
        
        // Prepare memorandum data for download
        const createdMemorandum = responseData.data || {
          id: Math.random(),
          number: `MEM-${Date.now()}`,
          subject: formData.subject,
          recipient: formData.recipientDepartment,
          cost: formData.estimatedCost,
          status: 'DRAFT',
          generatedAt: new Date().toISOString(),
          affectedWards: formData.affectedWards,
          evidence: formData.evidenceSummary,
          content: formData.actions
        };

        // Auto-download the memorandum after brief delay
        setTimeout(async () => {
          await handleDownloadMemorandum(createdMemorandum);
        }, 500);
        
        // Pass the created memorandum data back to the parent
        if (onSuccess) {
          onSuccess(responseData.data);
        }
        
        setTimeout(() => onClose(), 2000);
      } else {
        throw new Error(responseData.error || 'Failed to create memorandum');
      }
      
    } catch (error: any) {
      console.error('❌ Error creating memorandum:', error);
      
      // Check for specific error messages
      if (error.message?.includes('401')) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('aspirant');
        setTimeout(() => {
          window.location.href = '/aspirant/login';
        }, 1500);
      } else if (error.response?.data?.error?.includes('Aspirant not found')) {
        toast.error('Aspirant ID mismatch. Please logout and login again.');
        localStorage.removeItem('aspirant');
      } else {
        toast.error(error.response?.data?.error || error.message || 'Failed to create memorandum');
      }
      
    } finally {
      setSubmitting(false);
    }
  };

  if (!aspirantId) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Create Official Memorandum</h2>
            <p className="text-sm text-gray-600">Using aspirant ID: {aspirantId}</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-gray-100 rounded"
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 h-[calc(90vh-80px)]">
          {/* Left Column - Form */}
          <div className="p-4 overflow-y-auto border-r">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Citizen-Reported Acute Water Shortage in [Wards] – Request for Urgent Intervention"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Start with "Citizen-Reported" for proper formatting</p>
              </div>
              
              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Department *
                </label>
                <select
                  name="recipientDepartment"
                  value={formData.recipientDepartment}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select CECM department...</option>
                  {RECIPIENT_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              
              {/* Evidence Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Evidence Summary *
                </label>
                <textarea
                  name="evidenceSummary"
                  value={formData.evidenceSummary}
                  onChange={handleInputChange}
                  className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe the issue based on citizen reports (e.g., 'no water supply for extended periods, affecting daily life, health, and livelihoods')"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Will be included in the evidence section with date range</p>
              </div>
              
              {/* Recommended Actions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recommended Actions (one per line)
                </label>
                <textarea
                  name="actions"
                  value={formData.actions}
                  onChange={handleActionsChange}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="Immediate: Deploy water bowsers to affected areas&#10;Medium-term: Repair identified water infrastructure&#10;Long-term: Develop sustainable water supply solutions"
                />
                <p className="text-xs text-gray-500 mt-1">Each line will become a numbered action item</p>
              </div>
              
              {/* Affected Wards */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Affected Wards *
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-300 rounded-lg">
                  {WARDS.map((ward) => (
                    <label key={ward} className="flex items-center p-1">
                      <input
                        type="checkbox"
                        checked={formData.affectedWards.includes(ward)}
                        onChange={() => handleWardToggle(ward)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">{ward}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Select all affected wards (minimum 1 required)</p>
              </div>
              
              {/* Estimated Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Cost (KES)
                </label>
                <input
                  type="number"
                  name="estimatedCost"
                  value={formData.estimatedCost}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min="0"
                  step="100000"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formatCurrency(formData.estimatedCost)}
                </p>
              </div>
              
              {/* Aspirant Info */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <Info size={14} className="text-gray-500 mr-2" />
                  <p className="text-sm font-medium text-gray-700">Creating memorandum as:</p>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Name:</span> {aspirantInfo.name}</p>
                  <p><span className="font-medium">Position:</span> {aspirantInfo.position}</p>
                  <p><span className="font-medium">ID:</span> {aspirantId}</p>
                  <p><span className="font-medium">Contact:</span> {aspirantInfo.phone}</p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FileText size={16} className="mr-2" />
                      Create Memorandum
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
          
          {/* Right Column - Preview & Info */}
          <div className="p-4 overflow-y-auto bg-gray-50">
            <div className="flex items-center mb-4">
              <AlertCircle size={16} className="text-blue-600 mr-2" />
              <h3 className="font-medium text-gray-900">Memorandum Format Information</h3>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">What will be generated:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Professional PDF with official letterhead format</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Unique memorandum number (e.g., MEM-CTG-2403-0014-0001)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Proper To/From/Date/Subject sections</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Evidence summary with date range (last 30 days)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Numbered recommended actions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Estimated budget section</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Data protection compliance statement</span>
                  </li>
                </ul>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Example Subject Format:</h4>
                <p className="text-sm bg-gray-50 p-3 rounded border">
                  Citizen-Reported Acute Water Shortage in London Ward, Biashara Ward, Kapkures – Request for Urgent Intervention
                </p>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">After Submission:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>PDF will be generated automatically</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Download will start automatically</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Memorandum will appear in your list with correct cost</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Modal will close automatically after download</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* ID Mapping Info */}
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Using aspirant ID: <span className="font-mono">{aspirantId}</span>
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                The estimated cost you enter will be displayed correctly in the memorandum list and PDF download.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMemorandumModal;
